import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { INSIGHT_RANGES } from "../../../shared/goals/insights";
import { Icon } from "../../components/ui/Icon";
import type { Dates } from "../../lib/ccApi";
import { fmt } from "./text";
import { datesOf, dayMon, daysWords, type PaceLine } from "./v2";

/** Command Center V2's shared parts (notes/COMMAND-CENTER-V2.md): the dates, the pace line, a status, a fold, the Explain line. */

const today = () => new Date().toISOString().slice(0, 10);
const labelOf = (d: Dates) => ("preset" in d ? (INSIGHT_RANGES.find(([k]) => k === d.preset)?.[1] ?? "") : "Custom");

/** One button that says which days, and a short menu with each choice's days. */
export function DatePick({ dates, onChange, launchedAt, readDays }: { dates: Dates; onChange: (d: Dates) => void; launchedAt: string | null; readDays?: [string | null, string | null] }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState({ from: "from" in dates ? dates.from : "", to: "to" in dates ? dates.to : today(), on: "from" in dates });
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  // Why: the read's own first and last day are Meta's; before a read lands the days are worked out the way Meta counts them.
  const shown = readDays?.[0] && readDays[1] ? daysWords([new Date(`${readDays[0]}T00:00:00`), new Date(`${readDays[1]}T00:00:00`)]) : daysWords(datesOf(dates, launchedAt));
  const pick = (d: Dates) => {
    onChange(d);
    setOpen(false);
  };
  return (
    <div className="dpick" ref={box}>
      <button type="button" className="dbtn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Icon name="calendar" />
        <span>
          <b>{labelOf(dates)}</b>
          <em>{shown}</em>
        </span>
        <Icon name="chev" />
      </button>
      {open && (
        <div className="dmenu" role="menu" aria-label="Dates">
          {INSIGHT_RANGES.map(([k, l]) => (
            <button type="button" role="menuitemradio" aria-checked={"preset" in dates && dates.preset === k} className="dopt" key={k} onClick={() => pick({ preset: k })}>
              <Icon name="check" />
              <span>{l}</span>
              <em>{daysWords(datesOf({ preset: k }, launchedAt))}</em>
            </button>
          ))}
          <div className="dsep" />
          <button type="button" role="menuitemradio" aria-checked={"from" in dates} className="dopt" onClick={() => setCustom((c) => ({ ...c, on: true }))}>
            <Icon name="check" />
            <span>Custom</span>
            <em>{"from" in dates ? daysWords(datesOf(dates, null)) : ""}</em>
          </button>
          {custom.on && (
            <form
              className="dcustom"
              onSubmit={(e) => {
                e.preventDefault();
                if (custom.from && custom.to && custom.from <= custom.to) pick({ from: custom.from, to: custom.to });
              }}
            >
              <label>
                From
                <input type="date" value={custom.from} max={today()} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
              </label>
              <label>
                To
                <input type="date" value={custom.to} max={today()} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
              </label>
              <button type="submit" className="btn sm ghost" disabled={!custom.from || !custom.to || custom.from > custom.to}>
                Show these days
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

/** A goal's status in one word, with its colour. */
export function Status({ kind, paused = false }: { kind: PaceLine["kind"] | "ready" | "planning"; paused?: boolean }) {
  const [cls, word] = paused ? ["ready", "Paused"] : kind === "on" ? ["on", "On pace"] : kind === "behind" ? ["behind", "Behind"] : kind === "done" ? ["done", "Finished"] : kind === "ready" ? ["ready", "Getting ready"] : ["ready", "New plan"];
  return (
    <span className={`st ${cls}`}>
      <i />
      {word}
    </span>
  );
}

/** So far, where it should be by today, and where it lands at this rate. */
export function PaceBar({ p, label, big = false }: { p: PaceLine; label: string; big?: boolean }) {
  const w = (v: number) => Math.max(0, Math.min(100, (100 * v) / Math.max(1, p.target)));
  const words = p.kind === "done" ? `Finished ${dayMon(p.end)}` : p.kind === "on" ? `On pace for about ${fmt(p.forecast)} by ${dayMon(p.end)}` : `At this rate, about ${fmt(p.forecast)} by ${dayMon(p.end)}`;
  const aria = p.kind === "done" ? `${fmt(p.got)} of ${fmt(p.target)} ${label}` : `${fmt(p.got)} of ${fmt(p.target)} ${label}. By today it should be ${fmt(p.expected)}. ${words}.`;
  return (
    <div className={`pace ${p.kind}${big ? " big" : ""}`}>
      <div className="pace-l">
        <span className="n">{fmt(p.got)}</span>
        <span className="of">
          of {fmt(p.target)} {label}
        </span>
        <em>{words}</em>
      </div>
      <div className="track" role="img" aria-label={aria}>
        <i className="fill" style={{ width: `${w(p.got)}%` }} />
        {p.kind !== "done" && (
          <>
            <i className="ghost" style={{ left: `${w(p.got)}%`, width: `${Math.max(0, w(p.forecast) - w(p.got))}%` }} />
            <u style={{ left: `${w(p.expected)}%` }} title={`Where it should be by today: ${fmt(p.expected)}`}>
              {big && <span>Today</span>}
            </u>
          </>
        )}
      </div>
      {big && p.kind !== "done" && (
        <div className="scale">
          <span>{dayMon(p.start)}</span>
          <span>{dayMon(p.end)}</span>
        </div>
      )}
    </div>
  );
}

/** The pace line drawn thin, for a goal's one line: so far, where it should be by today, where it lands. */
export function MiniPace({ p, steps }: { p: PaceLine | null; steps?: { done: number; total: number } }) {
  if (steps)
    return (
      <span className="mp ready" aria-label={`${steps.done} of ${steps.total} steps done`}>
        {Array.from({ length: steps.total }, (_, i) => (
          <i key={i} className={i < steps.done ? "ok" : "here"} />
        ))}
      </span>
    );
  if (!p) return <span className="mp" />;
  const w = (v: number) => Math.max(0, Math.min(100, (100 * v) / Math.max(1, p.target)));
  return (
    <span className={`mp ${p.kind}`} title={p.kind === "done" ? `${fmt(p.got)} of ${fmt(p.target)}` : `${fmt(p.got)} of ${fmt(p.target)}; ${fmt(p.expected)} by today keeps it on pace`}>
      <i className="f" style={{ width: `${w(p.got)}%` }} />
      {p.kind !== "done" && (
        <>
          <i className="g" style={{ left: `${w(p.got)}%`, width: `${Math.max(0, w(p.forecast) - w(p.got))}%` }} />
          <u style={{ left: `${w(p.expected)}%` }} />
        </>
      )}
    </span>
  );
}

/** A heading that opens or closes what is under it. */
export function Fold({ label, open, onToggle, children }: { label: ReactNode; open: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <>
      <button type="button" className={`fold${open ? " open" : ""}`} aria-expanded={open} onClick={onToggle}>
        {label}
        <Icon name="chev" />
      </button>
      {open && children}
    </>
  );
}

/**
 * The line from the part Explain started on to its question in the chat: it runs down the gap beside the chat,
 * redrawn as either scrolls, and shows only while both ends are on the screen.
 */
/** A dock opened near the top of a short page starts below its sticky place, so its box to type in sits under the fold:
 * scroll just enough to show its foot. A phone's bottom sheet is fixed and needs nothing. */
export function useDockInView(ref: RefObject<HTMLElement | null>, open: boolean) {
  useEffect(() => {
    const el = ref.current;
    if (!open || !el) return;
    const id = requestAnimationFrame(() => {
      const cs = getComputedStyle(el);
      if (cs.position !== "sticky") return;
      const r = el.getBoundingClientRect();
      const over = r.bottom - innerHeight;
      if (over > 0) window.scrollBy({ top: Math.min(over + 16, r.top - parseFloat(cs.top)), behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [ref, open]);
}

export function ExplainLine({ active }: { active: boolean }) {
  const [d, setD] = useState<{ path: string; a: [number, number]; b: [number, number] } | null>(null);
  useEffect(() => {
    if (!active) {
      setD(null);
      return;
    }
    let queued = 0;
    const draw = () => {
      queued = 0;
      const lit = document.querySelector<HTMLElement>("[data-lit].explaining");
      const chat = document.querySelector<HTMLElement>(".gchat.on");
      const asks = chat?.querySelectorAll<HTMLElement>(".cc-msg.me");
      const mark = asks?.[asks.length - 1];
      if (!lit || !chat || getComputedStyle(chat).position !== "sticky") return setD(null);
      const c = lit.getBoundingClientRect();
      const h = chat.getBoundingClientRect();
      const top = 66;
      if (c.bottom < top + 24 || c.top > innerHeight - 32) return setD(null);
      const btn = lit.querySelector<HTMLElement>(".btn.explaining")?.getBoundingClientRect();
      const y1 = Math.max(Math.max(c.top + 18, top), Math.min(btn ? btn.top + btn.height / 2 : c.top + 30, Math.min(c.bottom - 18, innerHeight - 8)));
      const m = mark?.getBoundingClientRect();
      const y2 = Math.max(h.top + 66, Math.min(m ? m.top + m.height / 2 : h.top + 80, h.bottom - 24));
      const x1 = c.right;
      const x2 = h.left;
      const xm = Math.round((x1 + x2) / 2);
      const dy = y2 - y1;
      const r = Math.min(10, Math.abs(dy) / 2, (x2 - x1) / 2);
      const sg = Math.sign(dy);
      const path = Math.abs(dy) < 1 ? `M${x1} ${y1}H${x2}` : `M${x1} ${y1}H${xm - r}Q${xm} ${y1} ${xm} ${y1 + sg * r}V${y2 - sg * r}Q${xm} ${y2} ${xm + r} ${y2}H${x2}`;
      setD({ path, a: [x1, y1], b: [x2, y2] });
    };
    const soon = () => {
      if (!queued) queued = requestAnimationFrame(draw);
    };
    soon();
    // Why: the chat's answer arrives and grows the feed without a scroll, so the line also checks a few times a second.
    const t = setInterval(soon, 400);
    addEventListener("scroll", soon, { passive: true, capture: true });
    addEventListener("resize", soon);
    return () => {
      clearInterval(t);
      cancelAnimationFrame(queued);
      removeEventListener("scroll", soon, { capture: true });
      removeEventListener("resize", soon);
    };
  }, [active]);
  if (!d) return null;
  return (
    <svg className="xline fs-norecord" aria-hidden="true">
      <path d={d.path} />
      <circle className="halo" cx={d.a[0]} cy={d.a[1]} r="7" />
      <circle cx={d.a[0]} cy={d.a[1]} r="3.5" />
      <circle cx={d.b[0]} cy={d.b[1]} r="3.5" />
    </svg>
  );
}
